import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Award, Users, BookOpen, Target, Star, TrendingUp, MapPin, Phone, Mail, Globe, CheckCircle, Lightbulb, Heart, Shield } from 'lucide-react';

const AboutUs = () => {
  const location = useLocation();

  // Handle hash navigation with proper offset for fixed navbar
  useEffect(() => {
    if (location.hash) {
      const sectionId = location.hash.substring(1);
      const element = document.querySelector(`#${sectionId}`);
      if (element) {
        setTimeout(() => {
          const navbarHeight = 140; // Adjust based on your navbar height
          const elementPosition = element.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - navbarHeight;

          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }, 100);
      }
    }
  }, [location]);

  const values = [
    {
      icon: Target,
      title: 'Excellence',
      description: 'We strive for excellence in every aspect of our teaching methodology and student support services.'
    },
    {
      icon: Heart,
      title: 'Student-Centric Approach',
      description: 'Every decision we make is centered around our students\' success and well-being.'
    },
    {
      icon: Lightbulb,
      title: 'Innovation',
      description: 'We continuously innovate our teaching methods to provide the most effective learning experience.'
    },
    {
      icon: Shield,
      title: 'Integrity',
      description: 'We maintain the highest standards of integrity in all our interactions and commitments.'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-slate-100 to-sky-50 pt-20 sm:pt-24">

      {/* Mission & Vision */}
      <section id="mission-vision" className="py-8 sm:py-10 bg-slate-50" style={{ scrollMarginTop: '100px' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h3 className="text-4xl font-bold text-slate-900 mb-6">Our Mission</h3>
              <p className="text-lg text-slate-700 mb-6 leading-relaxed">
                To empower civil service aspirants with comprehensive, practical, and result-oriented guidance that transforms their preparation journey into a path of success.
              </p>
              <div className="space-y-4">
                <div className="flex items-start">
                  <CheckCircle className="h-6 w-6 text-sky-400 mr-3 mt-1 flex-shrink-0" />
                  <span className="text-slate-700">To provide relevant, updated, and structured guidance through our UPSC, MPSC, and CAPF batches.</span>
                </div>
                <div className="flex items-start">
                  <CheckCircle className="h-6 w-6 text-sky-400 mr-3 mt-1 flex-shrink-0" />
                  <span className="text-slate-700">To connect aspirants with recent toppers through our unique mentorship program, so they learn from those who've just walked the path.</span>
                </div>
                <div className="flex items-start">
                  <CheckCircle className="h-6 w-6 text-sky-400 mr-3 mt-1 flex-shrink-0" />
                  <span className="text-slate-700">To focus on clarity, consistency, and personal attention — not just syllabus completion.</span>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-2xl p-8 shadow-xl border border-slate-200 hover:border-sky-400 transition-colors">
              <h3 className="text-4xl font-bold text-slate-900 mb-6">Our Vision</h3>
              <p className="text-lg text-slate-700 leading-relaxed">
                To build a reliable and practical ecosystem that helps every sincere aspirant prepare confidently,
                learn efficiently, and succeed with clarity in UPSC, MPSC, and CAPF exams.
              </p>
              <div className="mt-6 p-4 bg-gradient-to-r from-slate-50 to-sky-50 rounded-lg">
                <p className="text-slate-900 font-medium text-center">
                  "Empowering Dreams, Creating Leaders, Serving Nation"
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section id="team" className="py-8 sm:py-10 bg-gradient-to-r from-slate-50 to-sky-50" style={{ scrollMarginTop: '100px' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-6 sm:mb-8">
            <h3 className="text-4xl font-bold text-slate-900 mb-4">Meet Our Team</h3>
            <p className="text-xl text-slate-700 max-w-3xl mx-auto">
              Our team is the backbone of our commitment to excellence. Comprising seasoned educators,
              subject experts, retired bureaucrats, and passionate mentors who guide aspirants through
              their UPSC and MPSC preparation journey.
            </p>
          </div>

          {/* Directors Section */}
          <div id="directors" className="mb-8" style={{ scrollMarginTop: '100px' }}>
            <h4 className="text-2xl font-bold text-center text-slate-900 mb-8">Leadership Team</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
              <div className="bg-white rounded-2xl p-6 shadow-lg text-center hover:shadow-xl transition-all duration-300 border border-slate-200 hover:border-sky-400 group">
                <div className="relative w-24 h-24 mx-auto mb-4 overflow-hidden rounded-full border-4 border-slate-200 group-hover:border-sky-400 transition-colors">
                  <img
                    src="/images/mentors/pradip_gund.webp"
                    alt="Pradip Gund"
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                  <div className="w-full h-full bg-slate-900 rounded-full hidden items-center justify-center">
                    <Users className="h-12 w-12 text-white" />
                  </div>
                </div>
                <h5 className="text-xl font-bold text-slate-900 mb-2">Pradip Gund</h5>
                <p className="text-sky-400 font-semibold">Director</p>
              </div>

              <div className="bg-white rounded-2xl p-6 shadow-lg text-center hover:shadow-xl transition-all duration-300 border border-slate-200 hover:border-sky-400 group">
                <div className="relative w-24 h-24 mx-auto mb-4 overflow-hidden rounded-full border-4 border-slate-200 group-hover:border-sky-400 transition-colors">
                  <img
                    src="/images/mentors/ranjit_bhagare.webp"
                    alt="Ranjit Bhagare"
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                  <div className="w-full h-full bg-slate-900 rounded-full hidden items-center justify-center">
                    <Users className="h-12 w-12 text-white" />
                  </div>
                </div>
                <h5 className="text-xl font-bold text-slate-900 mb-2">Ranjit Bhagare</h5>
                <p className="text-sky-400 font-semibold">Director</p>
              </div>

              <div className="bg-white rounded-2xl p-6 shadow-lg text-center hover:shadow-xl transition-all duration-300 border border-slate-200 hover:border-sky-400 group">
                <div className="relative w-24 h-24 mx-auto mb-4 overflow-hidden rounded-full border-4 border-slate-200 group-hover:border-sky-400 transition-colors">
                  <img
                    src="/images/mentors/sumant.jpg"
                    alt="Sumant Kamble"
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                  <div className="w-full h-full bg-slate-900 rounded-full hidden items-center justify-center">
                    <Users className="h-12 w-12 text-white" />
                  </div>
                </div>
                <h5 className="text-xl font-bold text-slate-900 mb-2">Sumant Kamble</h5>
                <p className="text-sky-400 font-semibold">Director </p>
              </div>
            </div>
          </div>

          {/* UPSC Toppers & Faculty */}
          <div id="faculty" className="mb-16" style={{ scrollMarginTop: '140px' }}>
            <h4 className="text-2xl font-bold text-center text-slate-900 mb-8">Faculty</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-white rounded-xl p-6 shadow-lg text-center hover:shadow-xl transition-all duration-300 border border-slate-200 hover:border-sky-400 group">
                <div className="relative w-20 h-20 mx-auto mb-4 overflow-hidden rounded-full border-4 border-slate-200 group-hover:border-sky-400 transition-colors">
                  <img
                    src="/images/mentors/sumant.jpg"
                    alt="Sumant Kamble"
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                  <div className="w-full h-full bg-slate-700 rounded-full hidden items-center justify-center">
                    <Award className="h-10 w-10 text-white" />
                  </div>
                </div>
                <h5 className="text-lg font-bold text-slate-900 mb-2">Sumant Kamble</h5>
                <div className="space-y-1">
                  <span className="inline-block bg-sky-100 text-sky-800 text-xs font-medium px-2 py-1 rounded-full">
                    Economics, International Relations
                  </span>
                </div>
              </div>


              <div className="bg-white rounded-xl p-6 shadow-lg text-center hover:shadow-xl transition-all duration-300 border border-slate-200 hover:border-sky-400 group">
                <div className="relative w-20 h-20 mx-auto mb-4 overflow-hidden rounded-full border-4 border-slate-200 group-hover:border-sky-400 transition-colors">
                  <img
                    src="/images/mentors/ranjit.webp"
                    alt="Ranjeet Thipe"
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                  <div className="w-full h-full bg-slate-700 rounded-full hidden items-center justify-center">
                    <Award className="h-10 w-10 text-white" />
                  </div>
                </div>
                <h5 className="text-lg font-bold text-slate-900 mb-2">Ranjeet Thipe</h5>
                <div className="space-y-1">
                  <span className="inline-block bg-slate-100 text-slate-800 text-xs font-medium px-2 py-1 rounded-full">
                    Ethics, History, Security, Governance
                  </span>
                </div>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-lg text-center hover:shadow-xl transition-all duration-300 border border-slate-200 hover:border-sky-400 group">
                <div className="relative w-20 h-20 mx-auto mb-4 overflow-hidden rounded-full border-4 border-slate-200 group-hover:border-sky-400 transition-colors">
                  <img
                    src="/images/mentors/pallavi.webp"
                    alt="Pallavi Sangale"
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                  <div className="w-full h-full bg-slate-700 rounded-full hidden items-center justify-center">
                    <Award className="h-10 w-10 text-white" />
                  </div>
                </div>
                <h5 className="text-lg font-bold text-slate-900 mb-2">Pallavi Sangale</h5>
                <div className="space-y-1">
                  <span className="inline-block bg-emerald-100 text-emerald-800 text-xs font-medium px-2 py-1 rounded-full">
                    Essay, Anthropology
                  </span>
                </div>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-lg text-center hover:shadow-xl transition-all duration-300 border border-slate-200 hover:border-sky-400 group">
                <div className="relative w-20 h-20 mx-auto mb-4 overflow-hidden rounded-full border-4 border-slate-200 group-hover:border-sky-400 transition-colors">
                  <img
                    src="/images/mentors/payal.webp"
                    alt="Payal Muzumdar"
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                  <div className="w-full h-full bg-slate-700 rounded-full hidden items-center justify-center">
                    <Award className="h-10 w-10 text-white" />
                  </div>
                </div>
                <h5 className="text-lg font-bold text-slate-900 mb-2">Payal Muzumdar</h5>
                <div className="space-y-1">
                  <span className="inline-block bg-pink-100 text-pink-800 text-xs font-medium px-2 py-1 rounded-full">
                    Environment, Anthropology
                  </span>
                </div>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-lg text-center hover:shadow-xl transition-all duration-300 border border-slate-200 hover:border-sky-400 group">
                <div className="relative w-20 h-20 mx-auto mb-4 overflow-hidden rounded-full border-4 border-slate-200 group-hover:border-sky-400 transition-colors">
                  <img
                    src="/images/mentors/abhijit.webp"
                    alt="Abhijit Chaugule"
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                  <div className="w-full h-full bg-slate-800 rounded-full hidden items-center justify-center">
                    <Target className="h-10 w-10 text-white" />
                  </div>
                </div>
                <h5 className="text-lg font-bold text-slate-900 mb-2">Abhijit Chaugule</h5>
                <div className="space-y-1">
                  <span className="inline-block bg-red-100 text-red-800 text-xs font-medium px-2 py-1 rounded-full">
                    Geography, Polity
                  </span>
                </div>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-lg text-center hover:shadow-xl transition-all duration-300 border border-slate-200 hover:border-sky-400 group">
                <div className="relative w-20 h-20 mx-auto mb-4 overflow-hidden rounded-full border-4 border-slate-200 group-hover:border-sky-400 transition-colors">
                  <img
                    src="/images/mentors/aditya.webp"
                    alt="Aditya Kedari"
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                  <div className="w-full h-full bg-slate-800 rounded-full hidden items-center justify-center">
                    <Target className="h-10 w-10 text-white" />
                  </div>
                </div>
                <h5 className="text-lg font-bold text-slate-900 mb-2">Aditya Kedari</h5>
                <div className="space-y-1">
                  <span className="inline-block bg-cyan-100 text-cyan-800 text-xs font-medium px-2 py-1 rounded-full">
                    Society, Sociology
                  </span>
                </div>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-lg text-center hover:shadow-xl transition-all duration-300 border border-slate-200 hover:border-sky-400 group">
                <div className="relative w-20 h-20 mx-auto mb-4 overflow-hidden rounded-full border-4 border-slate-200 group-hover:border-sky-400 transition-colors">
                  <img
                    src="/images/mentors/swapnil.webp"
                    alt="Swapnil Kshirsagar"
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                  <div className="w-full h-full bg-slate-800 rounded-full hidden items-center justify-center">
                    <BookOpen className="h-10 w-10 text-white" />
                  </div>
                </div>
                <h5 className="text-lg font-bold text-slate-900 mb-2">Swapnil Kshirsagar</h5>
                <div className="space-y-1">
                  <span className="inline-block bg-violet-100 text-violet-800 text-xs font-medium px-2 py-1 rounded-full">
                    Current Affairs, Science
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section id="core-values" className="py-8 sm:py-10" style={{ scrollMarginTop: '100px' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-6 sm:mb-8">
            <h3 className="text-4xl font-bold text-slate-900 mb-4">Our Core Values</h3>
            <p className="text-xl text-slate-700 max-w-3xl mx-auto">
              The principles that guide everything we do and shape our commitment to student success
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => {
              const IconComponent = value.icon;
              return (
                <div key={index} className="text-center group">
                  <div className="bg-slate-900 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 transform group-hover:scale-110 transition-transform duration-200 group-hover:bg-sky-400">
                    <IconComponent className="h-10 w-10 text-white" />
                  </div>
                  <h4 className="text-2xl font-bold text-slate-900 mb-4">{value.title}</h4>
                  <p className="text-slate-700 leading-relaxed">{value.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutUs;